import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const BrowserContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'supported' ? '#28a745' : props.status === 'partial' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const IssueItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.severity === 'error' ? '#dc3545' : props.severity === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const BrowserInfo = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: #333;
  }
`;

const BrowserTest = () => {
  const [testResults, setTestResults] = useState({
    html5: {
      semantic: null,
      forms: null,
      media: null,
      storage: null
    },
    css3: {
      flexbox: null,
      grid: null,
      animations: null,
      variables: null
    },
    javascript: {
      es6: null,
      promises: null,
      modules: null,
      webWorkers: null
    },
    apis: {
      fetch: null,
      webSocket: null,
      localStorage: null,
      indexedDB: null
    }
  });

  const [issues, setIssues] = useState([]);
  const [browserInfo, setBrowserInfo] = useState({
    name: '',
    version: '',
    platform: '',
    userAgent: ''
  });

  useEffect(() => {
    // Get browser information
    const ua = navigator.userAgent;
    const browserName = detectBrowser(ua);
    const browserVersion = detectBrowserVersion(ua);
    
    setBrowserInfo({
      name: browserName,
      version: browserVersion,
      platform: navigator.platform,
      userAgent: ua
    });
  }, []);

  const detectBrowser = (ua) => {
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    if (ua.includes('Edge/')) return 'Edge';
    if (ua.includes('MSIE') || ua.includes('Trident/')) return 'Internet Explorer';
    return 'Unknown';
  };

  const detectBrowserVersion = (ua) => {
    const match = ua.match(/(Firefox|Chrome|Safari|Edge|MSIE|Trident)\/([0-9.]+)/);
    return match ? match[2] : 'Unknown';
  };

  const checkHTML5Support = () => {
    const results = {
      semantic: {
        supported: !!document.createElement('header').constructor,
        features: ['header', 'nav', 'main', 'article', 'section', 'aside', 'footer']
      },
      forms: {
        supported: !!document.createElement('input').type,
        features: ['email', 'tel', 'url', 'number', 'range', 'date', 'time']
      },
      media: {
        supported: !!document.createElement('video').canPlayType,
        features: ['video', 'audio', 'source', 'track']
      },
      storage: {
        supported: !!window.localStorage && !!window.sessionStorage,
        features: ['localStorage', 'sessionStorage']
      }
    };

    setTestResults(prev => ({ ...prev, html5: results }));

    const newIssues = [];
    if (!results.semantic.supported) {
      newIssues.push({ severity: 'error', message: 'HTML5 semantic elements not supported' });
    }
    if (!results.forms.supported) {
      newIssues.push({ severity: 'warning', message: 'HTML5 form features not fully supported' });
    }
    if (!results.media.supported) {
      newIssues.push({ severity: 'error', message: 'HTML5 media elements not supported' });
    }
    if (!results.storage.supported) {
      newIssues.push({ severity: 'error', message: 'HTML5 storage APIs not supported' });
    }

    return newIssues;
  };

  const checkCSS3Support = () => {
    const results = {
      flexbox: {
        supported: !!document.createElement('div').style.flex,
        features: ['display: flex', 'flex-direction', 'justify-content', 'align-items']
      },
      grid: {
        supported: !!document.createElement('div').style.grid,
        features: ['display: grid', 'grid-template-columns', 'grid-template-rows']
      },
      animations: {
        supported: !!document.createElement('div').style.animation,
        features: ['@keyframes', 'animation', 'transition']
      },
      variables: {
        supported: !!getComputedStyle(document.documentElement).getPropertyValue('--test'),
        features: ['CSS Custom Properties', 'var()']
      }
    };

    setTestResults(prev => ({ ...prev, css3: results }));

    const newIssues = [];
    if (!results.flexbox.supported) {
      newIssues.push({ severity: 'error', message: 'CSS3 Flexbox not supported' });
    }
    if (!results.grid.supported) {
      newIssues.push({ severity: 'warning', message: 'CSS3 Grid not supported' });
    }
    if (!results.animations.supported) {
      newIssues.push({ severity: 'warning', message: 'CSS3 Animations not supported' });
    }
    if (!results.variables.supported) {
      newIssues.push({ severity: 'warning', message: 'CSS3 Custom Properties not supported' });
    }

    return newIssues;
  };

  const checkJavaScriptSupport = () => {
    const results = {
      es6: {
        supported: (() => {
          try {
            eval('let test = 1; const test2 = 2; class Test {};');
            return true;
          } catch (e) {
            return false;
          }
        })(),
        features: ['let/const', 'classes', 'arrow functions', 'template literals']
      },
      promises: {
        supported: !!window.Promise,
        features: ['Promise', 'async/await', 'Promise.all', 'Promise.race']
      },
      modules: {
        supported: !!document.createElement('script').type,
        features: ['import/export', 'ES modules']
      },
      webWorkers: {
        supported: !!window.Worker,
        features: ['Web Workers', 'Shared Workers']
      }
    };

    setTestResults(prev => ({ ...prev, javascript: results }));

    const newIssues = [];
    if (!results.es6.supported) {
      newIssues.push({ severity: 'error', message: 'ES6 features not supported' });
    }
    if (!results.promises.supported) {
      newIssues.push({ severity: 'error', message: 'Promises not supported' });
    }
    if (!results.modules.supported) {
      newIssues.push({ severity: 'warning', message: 'ES modules not supported' });
    }
    if (!results.webWorkers.supported) {
      newIssues.push({ severity: 'warning', message: 'Web Workers not supported' });
    }

    return newIssues;
  };

  const checkAPISupport = () => {
    const results = {
      fetch: {
        supported: !!window.fetch,
        features: ['fetch', 'Request', 'Response']
      },
      webSocket: {
        supported: !!window.WebSocket,
        features: ['WebSocket', 'WebSocket API']
      },
      localStorage: {
        supported: !!window.localStorage,
        features: ['localStorage', 'sessionStorage']
      },
      indexedDB: {
        supported: !!window.indexedDB,
        features: ['IndexedDB', 'IDBRequest', 'IDBTransaction']
      }
    };

    setTestResults(prev => ({ ...prev, apis: results }));

    const newIssues = [];
    if (!results.fetch.supported) {
      newIssues.push({ severity: 'error', message: 'Fetch API not supported' });
    }
    if (!results.webSocket.supported) {
      newIssues.push({ severity: 'warning', message: 'WebSocket API not supported' });
    }
    if (!results.localStorage.supported) {
      newIssues.push({ severity: 'error', message: 'localStorage not supported' });
    }
    if (!results.indexedDB.supported) {
      newIssues.push({ severity: 'warning', message: 'IndexedDB not supported' });
    }

    return newIssues;
  };

  const runAllTests = () => {
    setIssues([]);
    
    const html5Issues = checkHTML5Support();
    const css3Issues = checkCSS3Support();
    const javascriptIssues = checkJavaScriptSupport();
    const apiIssues = checkAPISupport();

    setIssues([
      ...html5Issues,
      ...css3Issues,
      ...javascriptIssues,
      ...apiIssues
    ]);
  };

  const getStatusFromValue = (value) => {
    if (!value) return 'error';
    return value.supported ? 'supported' : 'unsupported';
  };

  return (
    <BrowserContainer>
      <TestSection>
        <h3>Browser Compatibility Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Browser Tests
        </TestButton>

        <BrowserInfo>
          <h4>Current Browser Information</h4>
          <p>Browser: {browserInfo.name} {browserInfo.version}</p>
          <p>Platform: {browserInfo.platform}</p>
          <p>User Agent: {browserInfo.userAgent}</p>
        </BrowserInfo>

        <TestResults>
          <h3>HTML5 Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.html5.semantic)}>
              <h4>Semantic Elements</h4>
              <p>
                {testResults.html5.semantic ? 
                  `${testResults.html5.semantic.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.html5.forms)}>
              <h4>Form Features</h4>
              <p>
                {testResults.html5.forms ? 
                  `${testResults.html5.forms.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.html5.media)}>
              <h4>Media Elements</h4>
              <p>
                {testResults.html5.media ? 
                  `${testResults.html5.media.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.html5.storage)}>
              <h4>Storage APIs</h4>
              <p>
                {testResults.html5.storage ? 
                  `${testResults.html5.storage.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>CSS3 Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.css3.flexbox)}>
              <h4>Flexbox</h4>
              <p>
                {testResults.css3.flexbox ? 
                  `${testResults.css3.flexbox.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.css3.grid)}>
              <h4>Grid</h4>
              <p>
                {testResults.css3.grid ? 
                  `${testResults.css3.grid.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.css3.animations)}>
              <h4>Animations</h4>
              <p>
                {testResults.css3.animations ? 
                  `${testResults.css3.animations.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.css3.variables)}>
              <h4>Custom Properties</h4>
              <p>
                {testResults.css3.variables ? 
                  `${testResults.css3.variables.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>JavaScript Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.javascript.es6)}>
              <h4>ES6 Features</h4>
              <p>
                {testResults.javascript.es6 ? 
                  `${testResults.javascript.es6.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.javascript.promises)}>
              <h4>Promises</h4>
              <p>
                {testResults.javascript.promises ? 
                  `${testResults.javascript.promises.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.javascript.modules)}>
              <h4>ES Modules</h4>
              <p>
                {testResults.javascript.modules ? 
                  `${testResults.javascript.modules.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.javascript.webWorkers)}>
              <h4>Web Workers</h4>
              <p>
                {testResults.javascript.webWorkers ? 
                  `${testResults.javascript.webWorkers.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          <h3>Web APIs Support</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue(testResults.apis.fetch)}>
              <h4>Fetch API</h4>
              <p>
                {testResults.apis.fetch ? 
                  `${testResults.apis.fetch.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.apis.webSocket)}>
              <h4>WebSocket API</h4>
              <p>
                {testResults.apis.webSocket ? 
                  `${testResults.apis.webSocket.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.apis.localStorage)}>
              <h4>localStorage</h4>
              <p>
                {testResults.apis.localStorage ? 
                  `${testResults.apis.localStorage.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue(testResults.apis.indexedDB)}>
              <h4>IndexedDB</h4>
              <p>
                {testResults.apis.indexedDB ? 
                  `${testResults.apis.indexedDB.supported ? 'Supported' : 'Not Supported'}` : 
                  'Not tested'}
              </p>
            </TestCard>
          </TestGrid>

          {issues.length > 0 && (
            <>
              <h3>Compatibility Issues</h3>
              <IssueList>
                {issues.map((issue, index) => (
                  <IssueItem key={index} severity={issue.severity}>
                    {issue.message}
                  </IssueItem>
                ))}
              </IssueList>
            </>
          )}
        </TestResults>
      </TestSection>
    </BrowserContainer>
  );
};

export default BrowserTest; 